import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { v4 as uuid } from 'uuid'
import {
  FlowCore,
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowTransition,
} from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { recursiveCleanObject } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvcCreateFestivalContainer', {
  festivalCreationFailed: 'Impossible de créer le festival. Veuillez réessayer plus tard.',
  noOffersChosen: 'Vous devez choisir au moins une offre',
})

Locale.getInstance().registerKeySet('en', 'lvcCreateFestivalContainer', {
  festivalCreationFailed: 'Failed to create festival. Please try again later.',
  noOffersChosen: 'You have to choose at least one offer',
})

export interface LvcCreateFestivalContainer {}

const CREATE_FESTIVAL_STEPS = {
  CREATE_FESTIVAL_SEARCH_OR_ADD: 'createEventSearchOrAdd',
  CREATE_FESTIVAL_DESCRIPTION: 'createEventDescription',
  CREATE_FESTIVAL_DETAILS: 'createEventDetails',
  CREATE_FESTIVAL_IMAGE: 'createEventAddImage',
  ADD_FESTIVAL_CONTRIBUTORS: 'addEventContributors',
  CREATE_FESTIVAL_CONFIRMATION: 'eventConfirmation',
  CREATE_FESTIVAL_FIRST_RECAP: 'eventFirstRecap',
  ADD_EVENT_TO_FESTIVAL_CONFIRMATION: 'addedEventConfirmation',
  ADD_REPRESENTATION: 'addRepresentation',
  FINAL_FESTIVAL_RECAP: 'finalEventRecap',
  FESTIVAL_SUBMITTED: 'eventSubmitted',
}

const CORE_CONFIG: IFlowCoreConfig = {
  initialStep: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD,
  eventType: 'festival',
  calcProgress: (data: any, currentStep: IFlowTransition, transitions: IFlowTransition[]) => {
    let indexOf = transitions.indexOf(currentStep)

    return Math.round(100 * (indexOf / (transitions.length - 1)))
  },
  transitions: [
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD,
      elementName: (contextData: any) => `

        <lvc-create-event-search-or-add
          flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD}"
          flow-group="${contextData.flowGroup}"
          data-type="festival"
        ></lvc-create-event-search-or-add>
  		`,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DESCRIPTION
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DESCRIPTION,
      elementName: (contextData: any) => `
      <lvc-create-event-description
        flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DESCRIPTION}"
        flow-group="${contextData.flowGroup}"
			  data-type="festival"
      ></lvc-create-event-description>
		`,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DETAILS
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DETAILS,
      elementName: (contextData: any) => `
			<lvc-create-event-add-representation
			  flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DETAILS}"
			  flow-group="${contextData.flowGroup}"
			  data-type="festival"
			></lvc-create-event-add-representation>
		`,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_IMAGE
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DESCRIPTION
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_IMAGE,
      elementName: (contextData: any) => `
			<lvc-create-event-add-image
			  flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_IMAGE}"
			  flow-group="${contextData.flowGroup}"
			  data-type="festival"
			></lvc-create-event-add-image>
		`,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.ADD_FESTIVAL_CONTRIBUTORS
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DETAILS
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.ADD_FESTIVAL_CONTRIBUTORS,
      elementName: (contextData: any) => `
      <lvc-create-event-add-contributors flow-step="${CREATE_FESTIVAL_STEPS.ADD_FESTIVAL_CONTRIBUTORS}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-add-contributors>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_IMAGE
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_CONFIRMATION,
      elementName: (contextData: any) => `
      <lvc-create-event-final-recap 
        data-confirmation-mode="true" 
        flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_CONFIRMATION}" 
        flow-group="${contextData.flowGroup}"
      ></lvc-create-event-final-recap>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP,
      elementName: (contextData: any) => `
      <lvc-create-event-representations
        flow-step="${CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP}"
        flow-group="${contextData.flowGroup}"
        data-type="festival"
      ></lvc-create-event-representations>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_CONFIRMATION
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.ADD_REPRESENTATION,
      elementName: (contextData: any) => `
      <lvc-create-event-add-representation flow-step="${CREATE_FESTIVAL_STEPS.ADD_REPRESENTATION}" flow-group="${contextData.flowGroup}">
      </lvc-create-event-add-representation>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_DESCRIPTION
      },
    },

    {
      stepName: CREATE_FESTIVAL_STEPS.ADD_EVENT_TO_FESTIVAL_CONFIRMATION,
      elementName: (contextData: any) => `
      <lvc-create-event-final-recap
        data-confirmation-mode="true"
        flow-step="${CREATE_FESTIVAL_STEPS.ADD_EVENT_TO_FESTIVAL_CONFIRMATION}" 
        flow-group="${contextData.flowGroup}"
        data-adding-events="true"
      ></lvc-create-event-final-recap>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP,
      elementName: (contextData: any) => `
        <lvc-create-event-final-recap
        flow-step="${CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP}"
        flow-group="${contextData.flowGroup}"
        data-type="festival"
      ></lvc-create-event-final-recap>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FESTIVAL_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        if (data?.control_modifyingExistingEvent) {
          return CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_FIRST_RECAP
        }
        return CREATE_FESTIVAL_STEPS.ADD_FESTIVAL_CONTRIBUTORS
      },
    },
    {
      stepName: CREATE_FESTIVAL_STEPS.FESTIVAL_SUBMITTED,
      elementName: (contextData: any) => `
      <lvc-create-event-submitted
        flow-step="${CREATE_FESTIVAL_STEPS.FESTIVAL_SUBMITTED}"
        flow-group="${contextData.flowGroup}"
        data-type="festival"
      ></lvc-create-event-submitted>
    `,
      transitionNext: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FESTIVAL_SUBMITTED
      },
      transitionPrev: async (data: any) => {
        return CREATE_FESTIVAL_STEPS.FINAL_FESTIVAL_RECAP
      },
    },
  ] as IFlowTransition[],

  onSave: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const draftData = data

    // if we are on the 'search or add' step, (first step) we don't want to save the data
    if (
      instance.getCurrentTransitionStep().stepName ===
      CREATE_FESTIVAL_STEPS.CREATE_FESTIVAL_SEARCH_OR_ADD
    ) {
      return
    }

    try {
      await MainModule.getInstance()
        .getAuth()
        .setUserCustomDocument(
          `festival_drafts/${instance.entryId}`,
          recursiveCleanObject({
            draftData,
            lastModified: new Date().toISOString(),
          })
        )
    } catch (error) {
      console.error('Error during festival draft saving:', error)
    }
  },
  onCommit: async (data: unknown, instance: IFlowCore): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const flowCore = MainModule.getInstance().getFlowManager().getCore('festivalCreation')

    // Call the backend to create the festival
    const response = await mainModule
      .getAuth()
      .createContentItem(flowCore?.entryId || uuid(), data, 'festival')

    if (!response.success) {
      // If unsuccessful, show error (draft is preserved)
      console.error('Error during festival creation:', response.message)
      mainModule.showToastAlert(
        Locale.getKey('lvcCreateFestivalContainer.festivalCreationFailed'),
        5000,
        true
      )
    } else {
      // If successful, proceed to next step
      console.log('Festival created successfully')
      flowCore?.next()
    }
  },
}

export class LvcCreateFestivalContainer
  extends MinzeElement
  implements MinzeElement, IFlowContainer
{
  _authHandler: any = null
  flowGroup: string = 'festivalCreation'
  attrs: Attrs = []
  reactive: Reactive = ['stepName']
  flowCore: IFlowCore | null = null
  renderElement: string = ''
  draftId: string | null = null

  async onReady() {
    const mainModule = MainModule.getInstance()
    this._authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', this._authHandler)
    await this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  handleAuthChanged = async (data: any): Promise<void> => {
    if (!data.isLoggedIn) {
      console.warn('User is not logged in. Aborting draft fetch.')
      return
    }
    const mainModule = MainModule.getInstance()

    this.flowCore = new FlowCore({
      ...CORE_CONFIG,
      flowContainer: this,
    } as IFlowCoreConfig)
    mainModule.getFlowManager().putCore(this.flowCore)

    // Get the draftId from URL parameters.
    const urlParams = new URLSearchParams(window.location.search)
    this.draftId = urlParams.get('draftId')

    if (this.draftId) {
      try {
        const draftData = await mainModule.getAuth().fetchDraftFestivalById(this.draftId)
        if (draftData) {
          this.flowCore.entryId = this.draftId
          // Load the draft data before initialization.
          this.flowCore.loadDraft(draftData)
        }
      } catch (error) {
        console.error(`Error fetching draft for draftId ${this.draftId}:`, error)
      }
    } else {
      this.flowCore.entryId = uuid()
    }

    // draft data is loaded, initialize FlowCore.
    await this.flowCore.initialize()

    // If a draft was loaded, skip the first step because the first step is the search step.
    if (this.draftId) {
      this.flowCore.next()
    }

    // Notify footer navigation that the flow is ready.
    Minze.cast('lvc:flowcore-ready', this.flowCore)
  }

  async onContainerUpdate(data: any, step: IFlowTransition): Promise<void> {
    window.scrollTo(0, 0)
    let el: string = step?.elementName?.({ flowGroup: this.flowGroup }) || '<span>no element</span>'
    this.renderElement = el
    this.stepName = step.stepName
    this.rerender()
  }

  html = () => {
    let progress: number = this.flowCore?.getProgress() ?? 0
    return `
		<div class="lvc-create-festival-container">
		  <div class="lvc-content">
  			${this.renderElement}
			</div>
			<div class="lvc-footer-container">
				<lvc-footer-navigation
				  progress="${progress}"
				  flow-group="${this.flowGroup}"
				  step="${this.stepName}"
				  data-type="festival"
				></lvc-footer-navigation>
			</div>
		</div>
		`
  }

  css = () => {
    return `
		.lvc-create-festival-container {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			min-width: 100%;
			align-items: center;
			position: relative;
		}
		.lvc-content {
  		display: flex;
  		max-height: 100%;
  		flex-direction: column;
  		max-width: 600px;
      position: relative;
      padding-bottom: 200px;
		}
		.lvc-footer-container {
			display: flex;
			justify-content: center;
			flex-direction: column;
			margin-top: auto;
			width: 100%;
			position: fixed;
			bottom: 0;
		}`
  }

  eventListeners: EventListeners = []
}
